import type { DisplayWord } from "@circleofa/acim-engine"
import { CustomEventDispatcher } from "@utils/helpers/event-helper"
import { EventWrapper } from "@utils/helpers/event-wrapper"
import { calculateOffset } from "@utils/helpers/offset-top-recursive"

/**
 * Class responsible for moving the
 */
export const focusserMoverClass = "highlighter--focusser-mover"
export const focusserCompressorClass = "highlighter--focusser-compressor"

/**
 * Focusser is responsible for measuring the height and width.
 * The reason why the functions are split into three {@link Focusser.reset},
 * {@link Focuser.measure}, and {@link Focusser.slice}, is so that an
 * external interpreter can understand it. And this stops forced reflows.
 * If every item was responsible for its own measurements, rendering, and slicing,
 * there would be a reflow for each instantiation of the elements.
 *
 * By orchestrating it from higher up, we avoid unnecessary reflows
 */
export class Focusser {
    /**
     * Focusser mover transforms to shift within the compressor
     */
    public readonly focusserMover: HTMLElement
    /**
     * The height of compressor shrinks to focus on the sentence
     */
    public readonly focusserCompressor: HTMLElement
    public readonly firstLine: HTMLElement
    public readonly lastLine: HTMLElement
    public readonly focusEventWrapper: EventWrapper<FocusserEvents, SquintEventEmitter>

    #originalHeight = 0

    #invalidated = true
    #validationKey: undefined | unknown

    #lines: HTMLElement[]

    #measurements: {
        top: string
        bottom: number
        height: string
    } | null = null
    constructor(
        public readonly reader: HTMLElement,
        public readonly squintEventEmitter: SquintEventEmitter,
        public readonly firstWord: DisplayWord,
        public readonly lastWord: DisplayWord,
    ) {
        const focusserMover = reader.closest(`.${focusserMoverClass}`)
        if (focusserMover === null || !(focusserMover instanceof HTMLElement)) {
            throw new Error(`Parent did not have focusser mover class: ${focusserMoverClass}`)
        }
        this.focusserMover = focusserMover
        const focusserCompressor = focusserMover.closest(`.${focusserCompressorClass}`)
        if (focusserCompressor === null || !(focusserCompressor instanceof HTMLElement)) {
            throw new Error(
                `Parent did not have focusser compressor class that was a parent of mover: ${focusserCompressorClass}`,
            )
        }
        this.#lines = [...this.reader.querySelectorAll<HTMLElement>(".reader--line")]
        this.focusserCompressor = focusserCompressor

        this.firstLine = this.#getLine(firstWord, true)
        this.lastLine = this.#getLine(lastWord, false)

        this.focusEventWrapper = new EventWrapper(squintEventEmitter)
        this.focusEventWrapper.register({
            measure: this.measure.bind(this),
            reset: this.reset.bind(this),
            slice: this.slice.bind(this),
        })
    }

    /**
     * Invalidates if the key is different from the last
     * This is needed if we're doing something like infinity scroll
     * but we only want the new ones to load. If the {@link reset}
     * function gets called from a container of many of these,
     * technically all would re-render. So by having a key that
     * tells us if something that would affect the squint measurements
     * (e.g., container width) changed, we'll know to do this one
     * @param key some kind of equality checker
     */
    invalidateIfNew(key: unknown) {
        if (key !== this.#validationKey) {
            this.#validationKey = key
            this.#invalidated = true
        }
    }

    /**
     * This is used to force invalidation
     */
    invalidate() {
        this.#invalidated = true
        this.#validationKey = null
    }

    /**
     * Resets measurements, but only if invalidated
     */
    reset() {
        this.#removeAnimation()
        if (!this.#invalidated) return
        this.#measurements = null
        this.focusserCompressor.style.height = "auto"
        this.focusserMover.style.translate = "0"
        this.#originalHeight = parseFloat(getComputedStyle(this.focusserCompressor).height)
    }

    /**
     * Takes measurements, but only if invalidated
     */
    measure() {
        if (!this.#invalidated) return
        if (this.#measurements !== null) throw new Error("Reset has not been called!")
        const top = calculateOffset(this.firstLine, this.focusserMover, "offsetTop")
        const bottom =
            calculateOffset(this.lastLine, this.focusserMover, "offsetTop") +
            this.lastLine.offsetHeight
        const height = bottom - top

        const topDx = top
        const bottomDx = bottom - height

        const baseline = parseFloat(getComputedStyle(this.reader).fontSize)
        const idealPadding = 0 * baseline // em

        const topPadding = Math.min(topDx, idealPadding) / baseline
        const bottomPadding = Math.min(bottomDx, idealPadding) / baseline // covert back to em

        this.#measurements = {
            top: `calc(${topPadding}em - ${top}px)`,
            height: `calc(${height}px + ${topPadding + bottomPadding}em)`,
            bottom,
        }
    }

    /**
     * Slices to focus, again only if invalidated
     */
    slice() {
        if (!this.#invalidated) return
        if (this.#measurements === null) throw new Error("Measurements not set")
        this.focusserCompressor.style.height = this.#measurements.height
        this.focusserMover.style.translate = `0px ${this.#measurements.top}`
        this.fadeOuterLines()
        this.#invalidated = false
    }

    contract() {
        if (this.#invalidated) throw new Error("Contract was called by it was invalidated")
        if (this.#measurements === null)
            throw new Error("Contract was called but measurements not set")
        this.#applyAnimation()
        this.focusserCompressor.style.height = this.#measurements.height
        this.focusserMover.style.translate = `0px ${this.#measurements.top}`
        this.fadeOuterLines()
    }

    /**
     * Expand back to its natural form
     */
    expand() {
        this.#applyAnimation()
        this.focusserCompressor.style.height = this.#originalHeight + "px"
        this.focusserMover.style.translate = `0px 0px`
        this.unfadeOuterLines()
    }

    fadeOuterLines() {
        const firstWeight = Number(this.firstLine.dataset.startingWeight)
        const lastWeight = Number(this.lastLine.dataset.startingWeight)
        for (const line of this.#lines) {
            const weight = Number(line.dataset.startingWeight)
            line.style.opacity = firstWeight <= weight && weight <= lastWeight ? "1" : "0.5"
        }
    }

    unfadeOuterLines() {
        const firstWeight = Number(this.firstLine.dataset.startingWeight)
        const lastWeight = Number(this.lastLine.dataset.startingWeight)
        for (const line of this.#lines) {
            const weight = Number(line.dataset.startingWeight)
            line.style.opacity = firstWeight <= weight && weight <= lastWeight ? "1" : "0.7"
        }
        // this.#lines.forEach(line => line.style.opacity = "1")
    }

    #getLine(word: DisplayWord, first: boolean) {
        const path = word.parentWeight.path
        const spans = this.reader.querySelectorAll(`span.reader--line[data-notation="${path}"]`)
        if (spans.length === 0) throw new Error(`Reader line with path ${path} not found`)
        return <HTMLSpanElement>spans[first ? 0 : spans.length - 1]
    }

    #applyAnimation() {
        const transition = "0.2s ease-in-out"
        this.focusserCompressor.style.transition = "height " + transition
        this.focusserMover.style.transition = "translate " + transition
        this.#lines.forEach((line) => (line.style.transition = "opacity" + transition))
    }

    #removeAnimation() {
        this.focusserCompressor.style.transition = ""
        this.focusserMover.style.transition = ""
        this.#lines.forEach((line) => (line.style.transition = ""))
    }

    destroy() {
        this.focusEventWrapper.removeListeners()
    }
}

/**
 * Focusser events
 */
type FocusserEvents = {
    reset: null
    measure: null
    slice: null
}

/**
 * Responsible for orchestrating the {@link Focusser} elements
 */
export class SquintEventEmitter extends CustomEventDispatcher<FocusserEvents> {
    constructor() {
        super()
    }

    slice() {
        this.dispatch("slice")
    }
    measure() {
        this.dispatch("measure")
    }
    reset() {
        this.dispatch("reset")
    }
}

export class FocusExpander extends CustomEventDispatcher<{ expand: void; contract: void }> {}
